import Meatballs from "@/Icons/Meatballs";
import { useLogoUpload } from "@/api/hooks/useLogoUpload";
import { Button } from "@/components/button";
import { ALLOWED_FILE_TYPES } from "@/constants/fileUpload";
import { CLIPPED_BUTTON_STYLE } from "@/constants/general";
import { useTrans } from "@/translations";

import { useEffect, useMemo, useRef, useState } from "react";

import { twMerge } from "tailwind-merge";

type LogoTileVariant = "rounded" | "clipped";

type LogoTileUploadConfig = {
  getUploadUrl: (
    filename: string,
  ) => Promise<{ url: string; oldFileName?: string }>;
  cleanUpLogoFiles: (oldFileName: string) => Promise<void>;
  deleteLogo: (fileName: string, options: { onSuccess: () => void }) => void;
  onComplete: () => void;
  currentFileName: string | undefined;
};

type Props = {
  logoUrl: string | null | undefined;
  displayName: string;
  size: number;
  variant?: LogoTileVariant;
  className?: string;
  enableFileMenu?: boolean;
  buttonPosition?: "left" | "right";
  uploadConfig?: LogoTileUploadConfig;
};

type LogoTileWithFileMenuOverlayProps = LogoTileBaseProps & {
  buttonPosition: "left" | "right";
  uploadConfig: LogoTileUploadConfig;
};

type LogoTileBaseProps = {
  logoUrl: string | null | undefined;
  fallbackLetter: string;
  fontSize: string;
  size: number;
  variant: LogoTileVariant;
  className?: string;
  fillContainer?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
};

const LogoTileBase: React.FC<LogoTileBaseProps> = ({
  logoUrl,
  fallbackLetter,
  fontSize,
  size,
  variant,
  className,
  fillContainer = false,
  onClick,
  children,
}) => (
  <div
    className={twMerge(
      "flex items-center justify-center",
      fillContainer && "h-full w-full",
      onClick && "group cursor-pointer",
      variant === "rounded" && "rounded-md",
      variant === "clipped" && CLIPPED_BUTTON_STYLE,
      !logoUrl && "bg-lime",
      className,
    )}
    style={{
      ...(!fillContainer ? { width: size, height: size } : {}),
      ...(logoUrl
        ? {
            backgroundImage: `url(${logoUrl})`,
            backgroundSize: "contain",
            backgroundPosition: "50%",
            backgroundRepeat: "no-repeat",
          }
        : {}),
    }}
    onClick={onClick}
  >
    {!logoUrl && (
      <span className={twMerge(fontSize, "font-semibold select-none")}>
        {fallbackLetter}
      </span>
    )}
    {children}
  </div>
);

const LogoTileWithFileMenuOverlay: React.FC<
  LogoTileWithFileMenuOverlayProps
> = ({
  logoUrl,
  fallbackLetter,
  fontSize,
  size,
  variant,
  className,
  buttonPosition,
  uploadConfig,
}) => {
  const { t } = useTrans();
  const [showButtons, setShowButtons] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showButtons) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowButtons(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [showButtons]);

  const {
    inputRef,
    triggerFileUpload,
    onFileChange,
    handleDelete,
    isUploading,
    onAddFile,
  } = useLogoUpload(uploadConfig);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isUploading) setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.relatedTarget as Node)
    ) {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragActive(false);
    if (isUploading) return;
    setShowButtons(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      onAddFile(file).catch(() => {
        // errors are surfaced via modal/alert inside onAddFile
      });
    }
  };

  const buttons = useMemo(() => {
    const items = [
      {
        title: t("actions:uploadLogo"),
        onClick: () => {
          triggerFileUpload();
          setShowButtons(false);
        },
        style: "",
      },
    ];

    if (uploadConfig.currentFileName) {
      items.push({
        title: t("actions:deleteLogo"),
        onClick: handleDelete,
        style: "text-carrot-red",
      });
    }

    return items;
  }, [t, triggerFileUpload, handleDelete, uploadConfig.currentFileName]);

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{ width: size, height: size }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={inputRef}
        className="hidden"
        onChange={onFileChange}
        accept={ALLOWED_FILE_TYPES.map((ext) => `.${ext}`).join(", ")}
        multiple={false}
        data-1p-ignore
      />

      <LogoTileBase
        logoUrl={logoUrl}
        fallbackLetter={fallbackLetter}
        fontSize={fontSize}
        size={size}
        variant={variant}
        className={className}
        fillContainer
        onClick={() => {
          if (!isUploading) setShowButtons((prev) => !prev);
        }}
      >
        {!isUploading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className={twMerge(
                "bg-grey1 absolute inset-0 opacity-80",
                isDragActive ? "block" : "hidden group-hover:block",
              )}
            />
            {isDragActive ? (
              <span className="relative z-10 text-center text-xs leading-tight font-semibold select-none">
                {t("actions:dropToUpload")}
              </span>
            ) : (
              <div className="absolute top-[calc(50%-5px)] left-[calc(50%-2px)] hidden rotate-90 group-hover:block">
                <Meatballs />
              </div>
            )}
          </div>
        )}
      </LogoTileBase>

      {showButtons && (
        <div
          className={twMerge(
            "border-grey2 absolute top-full z-20 mt-1 border shadow-sm",
            buttonPosition === "left" ? "left-0" : "right-0",
          )}
        >
          {buttons.map(({ onClick, title, style }, index) => (
            <Button
              label={title}
              key={title}
              className={twMerge(
                "hover:bg-grey1 flex h-12 w-40 items-center border bg-white pl-4 text-sm",
                index < buttons.length - 1 && "border-b-0",
                style,
              )}
              onClick={onClick}
              clipped={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const LogoTile: React.FC<Props> = ({
  logoUrl,
  displayName,
  size,
  variant = "rounded",
  className,
  enableFileMenu = false,
  buttonPosition = "left",
  uploadConfig,
}) => {
  const fallbackLetter = useMemo(
    () => displayName.slice(0, 1).toUpperCase(),
    [displayName],
  );

  const fontSize = size < 50 ? "text-xl" : "text-4xl";

  if (enableFileMenu && uploadConfig) {
    return (
      <LogoTileWithFileMenuOverlay
        logoUrl={logoUrl}
        fallbackLetter={fallbackLetter}
        fontSize={fontSize}
        size={size}
        variant={variant}
        className={className}
        buttonPosition={buttonPosition}
        uploadConfig={uploadConfig}
      />
    );
  }

  return (
    <LogoTileBase
      logoUrl={logoUrl}
      fallbackLetter={fallbackLetter}
      fontSize={fontSize}
      size={size}
      variant={variant}
      className={className}
    />
  );
};
